import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Logo, LogoFull } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grid-bg">
      <div className="absolute inset-0 hero-glow" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-[#0C1424] border border-slate-800 rounded-lg p-8"
      >
        <Link to="/" className="flex justify-center mb-6"><LogoFull size={92} /></Link>
        <h1 className="font-heading text-2xl font-semibold text-center">Welcome back</h1>
        <p className="text-slate-400 text-sm text-center mt-1">Log in to your Warden dashboard</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
          <div>
            <label className="text-sm text-slate-400">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full bg-[#030812] border border-slate-800 text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyanbrand transition-all"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 w-full bg-[#030812] border border-slate-800 text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyanbrand transition-all"
              placeholder="••••••••"
            />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <div className="text-right -mt-1">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-slate-400 hover:text-cyanbrand transition-colors">
              Forgot password?
            </Link>
          </div>
          <button
            data-testid="login-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold py-3 rounded-md transition-all disabled:opacity-60"
            style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
          >
            {loading && <Loader2 size={18} className="animate-spin" />} Log in
          </button>
        </form>
        <p className="text-sm text-slate-400 text-center mt-6">
          No account? <Link to="/signup" className="text-cyanbrand hover:underline">Get started free</Link>
        </p>
        <p className="text-xs text-slate-600 text-center mt-4">Demo: demo@warden.security / Demo1234!</p>
      </motion.div>
    </div>
  );
}
